import React, { useMemo } from 'react'
import { Download04 } from '@untitled-ui/icons-react'
import { isApiMediaUrl, openMedia, safeTypedBlob } from '@src/services/media'
import Overlay from './Overlay'
import { useMediaBlob, useObjectUrl } from './useMedia'

// Reusable in-app previewer for an uploaded document. Images render in <img>,
// everything else (PDF) in an <iframe> using the browser's native viewer — no
// download, no third-party library.
//
// Two deliberate choices:
//  1. Renders its OWN portal overlay (components/Overlay) instead of MyModal.
//     MyModal wraps content in a MUI <Modal> whose focus-trap fights <iframe>
//     content.
//  2. Fetches the file and renders it from a same-origin blob: URL instead of
//     pointing the iframe straight at the cross-origin uploads URL. Chrome won't
//     let you scroll/zoom a PDF embedded cross-origin (page on :3003, file on
//     :31234); a blob: URL is same-origin so the native viewer is fully
//     interactive. We also force the correct MIME so it always previews inline
//     even if the uploads proxy returns application/octet-stream.
//
// Because the blob: URL is same-origin, what the iframe gets is typed by
// services/media safeTypedBlob: a type that could run script on this origin
// (an SVG or HTML shown as a document) is handed to the browser as a download
// instead. An <img> may show any image type (an SVG runs no script there).
//
// The fetch goes through services/media, which sends the Bearer only to the API
// origin. A URL on any other host is never requested: the viewer shows "file
// unavailable" and offers no "open in new tab" (that would be refused too).

const EXT_MIME = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
}

function guessMime(name, fallback) {
  const ext = (name || '').split('.').pop()?.toLowerCase()
  return EXT_MIME[ext] || (fallback && fallback !== 'application/octet-stream' ? fallback : '')
}

// The file as the viewer shows it: an image keeps its image type (for the
// <img>), anything else gets a type that runs no script here.
function shownBlob(raw, fileName, mimeType) {
  const type =
    guessMime(fileName, mimeType) ||
    (raw.type && raw.type !== 'application/octet-stream' ? raw.type : 'application/pdf')
  if (/^image\//i.test(type)) return raw.type === type ? raw : raw.slice(0, raw.size, type)
  return safeTypedBlob(raw, type)
}

export default function FileViewer({ open, onClose, url, fileName, mimeType }) {
  const allowed = Boolean(url) && isApiMediaUrl(url)
  // A URL off the API origin is never requested (rendered as unavailable).
  const loaded = useMediaBlob(open && allowed ? url : null)
  const shown = useMemo(
    () => (loaded.blob ? shownBlob(loaded.blob, fileName, mimeType) : null),
    [loaded.blob, fileName, mimeType]
  )
  const objectUrl = useObjectUrl(shown)
  const image = /^image\//i.test(shown?.type || '')
  const openInTab = () => openMedia(url, { type: shown?.type, fileName }).catch(() => {})

  return (
    <Overlay
      open={open}
      onClose={onClose}
      title={fileName || 'Document'}
      maxWidth={900}
      zIndex={99999}
      className="h-[85vh]"
      actions={
        allowed && (
          <button
            type="button"
            onClick={openInTab}
            title="Open in new tab"
            aria-label="Open in new tab"
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
          >
            <Download04 className="h-5 w-5" stroke="currentColor" />
          </button>
        )
      }
    >
      <div className="flex flex-1 items-center justify-center overflow-auto bg-gray-50 p-3">
        {!url ? (
          <p className="text-sm text-gray-500">No file to preview.</p>
        ) : !allowed ? (
          <p className="text-sm text-gray-500">This file is unavailable.</p>
        ) : loaded.status === 'error' ? (
          <p className="text-sm text-gray-500">
            Couldn’t load this document.{' '}
            <button type="button" onClick={openInTab} className="underline">
              Open in a new tab
            </button>
            .
          </p>
        ) : !objectUrl ? (
          <p className="text-sm text-gray-500">Loading document…</p>
        ) : image ? (
          <img src={objectUrl} alt={fileName || 'document'} className="max-h-full max-w-full object-contain" />
        ) : (
          <iframe src={objectUrl} title={fileName || 'document'} className="h-full w-full border-0" />
        )}
      </div>
    </Overlay>
  )
}
